/**
 * AITeamPreviewStrip — slim, dark-themed teaser strip for the homepage.
 * Surfaces the 5 AI Operations Division portraits + a one-line manifesto
 * + a CTA into /our-team. Designed to be dropped in just above the
 * "Our commitment" band on the homepage so the visitor's last impression
 * before the footer is "real team behind real systems."
 *
 * The "operated services" sub-line was originally an inline horizontal
 * tag row (JapanETCcard · JapanTollReceipts · JapanChangeMoney) which
 * overflowed the viewport on small phones (~390px). Per owner directive
 * Feb 2026, it is now a compact button that opens a modal letting the
 * visitor pick which service detail page to jump to — preventing any
 * horizontal-scroll behaviour on mobile.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowUpRight, Cpu, Layers, X } from "lucide-react";

import { AI_TEAM, ROLE_THEME } from "@/data/aiTeam";

const OPERATED_SERVICES = [
  {
    slug: "japanetccard",
    nameEn: "JapanETCcard",
    nameJa: "JapanETCcard",
    taglineEn: "ETC card support for foreign residents · Live since 2013",
    taglineJa: "外国人居住者向けETCカードサポート ・ 2013年〜運営中",
    color: "#5B2E83",
    status: "live",
  },
  {
    slug: "japantollreceipts",
    nameEn: "JapanTollReceipts",
    nameJa: "JapanTollReceipts",
    taglineEn: "AI-assisted toll receipts & corporate billing",
    taglineJa: "AI支援による高速料金レシート・法人請求対応",
    color: "#0E5FB0",
    status: "live",
  },
  {
    slug: "japanchangemoney",
    nameEn: "JapanChangeMoney",
    nameJa: "JapanChangeMoney",
    taglineEn: "Transparent currency exchange — planned launch 2026",
    taglineJa: "透明性のある両替サービス ・ 2026年公開予定",
    color: "#2F8F3C",
    status: "planned",
  },
];

export default function AITeamPreviewStrip({ lang }) {
  const isJa = lang === "ja";
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    if (!servicesOpen) return undefined;
    function onKey(e) {
      if (e.key === "Escape") setServicesOpen(false);
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [servicesOpen]);

  return (
    <section
      className="bg-[#0B4D8F] text-white border-y border-white/5"
      data-testid="home-ai-team-strip"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 ring-1 ring-white/15 mb-4">
            <Cpu className="h-3.5 w-3.5 text-[#FFD166]" strokeWidth={2.25} />
            <span className="text-[10.5px] tracking-[0.26em] font-mono uppercase font-bold text-[#FFD166]">
              {isJa ? "AI支援オペレーション" : "AI Operations Division"}
            </span>
          </div>
          <h2
            data-testid="home-ai-team-strip-headline"
            className={[
              isJa ? "font-ja" : "font-display",
              "text-white text-3xl md:text-[40px] font-bold tracking-tight leading-[1.1]",
            ].join(" ")}
          >
            {isJa
              ? "ひとつのAI支援チームが、3つのサービスすべてを支えています。"
              : "One five-member team. Three services. Same operating discipline."}
          </h2>
          <p className="mt-4 text-[14.5px] leading-[1.75] text-white/85 max-w-xl">
            {isJa
              ? "JapanETCcard・JapanTollReceipts・JapanChangeMoneyの3サービスを、同じAI支援ワークフローが支えています。シニアAIアナリスト、QAリード、ディレクターの各ロールが情報を整理し、最終承認の前に複数の確認ステップを通します。"
              : "JapanETCcard · JapanTollReceipts · JapanChangeMoney — every one of them is operated by the same five people (three Senior AI Analysts, one QA Lead, one Director). Every document passes through multiple sets of eyes before it ships."}
          </p>
          {/* Operated-services trigger — replaces the old inline tag row
              (which overflowed on mobile). Opens a modal with the 3
              services as selectable cards. */}
          <button
            type="button"
            onClick={() => setServicesOpen(true)}
            data-testid="home-ai-team-strip-services-trigger"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7AB8E8]/20 ring-1 ring-[#7AB8E8]/45 text-[11px] tracking-[0.18em] font-mono uppercase font-bold text-white hover:bg-[#7AB8E8]/30 hover:ring-[#7AB8E8]/70 transition-colors"
          >
            <Layers className="h-3.5 w-3.5 text-[#FFD166]" strokeWidth={2.25} />
            <span>{isJa ? "運営サービス3つ" : "3 operated services"}</span>
          </button>
          <Link
            to={`/${lang}/our-team`}
            data-testid="home-ai-team-cta"
            className="mt-6 ml-0 sm:ml-3 inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-5 py-2.5 rounded-full text-[12.5px] font-semibold hover:bg-[#F1F5F9] transition-colors"
          >
            {isJa ? "AIチームを見る" : "Meet the team"}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="lg:col-span-6">
          <ul className="grid grid-cols-5 gap-2 md:gap-3">
            {AI_TEAM.map((m, i) => {
              const theme = ROLE_THEME[m.role];
              return (
                <li key={m.slug} data-testid={`home-ai-team-portrait-${m.slug}`}>
                  <Link
                    to={`/${lang}/our-team`}
                    className="block relative rounded-xl overflow-hidden ring-1 ring-white/10 hover:ring-white/30 hover:-translate-y-0.5 transition-all aspect-square bg-[#0E5FB0]"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <img
                      src={m.portrait}
                      alt={isJa ? m.nameJa : m.nameEn}
                      className="block w-full h-full object-cover"
                      loading="lazy"
                      draggable={false}
                    />
                    <div
                      aria-hidden
                      className="absolute inset-0 bg-gradient-to-t from-[#5B2E83]/85 via-transparent to-transparent"
                    />
                    <span
                      aria-hidden
                      className="absolute top-0 left-0 right-0 h-[3px]"
                      style={{ backgroundColor: theme.hex }}
                    />
                    <div className="absolute bottom-1.5 left-0 right-0 text-center">
                      <div className="text-[11px] font-bold text-white tracking-tight">
                        {isJa ? m.nameJa : m.nameEn}
                      </div>
                      <div
                        className="text-[8px] tracking-[0.18em] font-mono uppercase font-bold hidden sm:block"
                        style={{ color: theme.hex }}
                      >
                        {isJa ? theme.labelJa : theme.labelEn}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Operated services modal — rendered through a portal to escape
          any ancestor transform / overflow-hidden context. */}
      {servicesOpen && typeof document !== "undefined" && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={isJa ? "運営サービス一覧" : "Operated services"}
          data-testid="home-ai-team-strip-services-modal"
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6 bg-[#5B2E83]/85 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setServicesOpen(false);
          }}
        >
          <div
            className="relative w-full max-w-md bg-white text-[#0F1D2D] rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#F1F5F9]">
              <div>
                <div className="text-[10px] tracking-[0.22em] font-mono uppercase font-bold text-[#5B2E83]">
                  {isJa ? "運営サービス" : "Operated services"}
                </div>
                <h3 className="font-display text-[18px] font-bold tracking-tight leading-tight mt-1">
                  {isJa ? "サービスを選択" : "Pick a service"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setServicesOpen(false)}
                data-testid="home-ai-team-strip-services-modal-close"
                aria-label={isJa ? "閉じる" : "Close"}
                className="h-9 w-9 rounded-full bg-[#F8F9FA] ring-1 ring-[#E2E8F0] inline-flex items-center justify-center hover:bg-[#F1F5F9] transition-colors"
              >
                <X className="h-4 w-4 text-[#0F1D2D]" strokeWidth={2.25} />
              </button>
            </div>
            <ul className="p-2.5">
              {OPERATED_SERVICES.map((svc) => (
                <li key={svc.slug}>
                  <Link
                    to={`/${lang}/services/${svc.slug}`}
                    onClick={() => setServicesOpen(false)}
                    data-testid={`home-ai-team-strip-services-modal-${svc.slug}`}
                    className="group flex items-start gap-3 px-3.5 py-3 rounded-xl hover:bg-[#F8F9FA] transition-colors"
                  >
                    <span
                      aria-hidden
                      className="mt-1 inline-block h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: svc.color }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-display text-[14.5px] font-bold tracking-tight text-[#0F1D2D]">
                          {isJa ? svc.nameJa : svc.nameEn}
                        </div>
                        {svc.status === "planned" && (
                          <span
                            className="text-[9px] tracking-[0.18em] font-mono uppercase font-bold px-1.5 py-0.5 rounded-full"
                            style={{
                              color: svc.color,
                              backgroundColor: `${svc.color}15`,
                            }}
                          >
                            {isJa ? "予定" : "Planned"}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[12px] leading-[1.5] text-[#475569]">
                        {isJa ? svc.taglineJa : svc.taglineEn}
                      </div>
                    </div>
                    <ArrowUpRight
                      className="h-4 w-4 text-[#94A3B8] group-hover:text-[#0F1D2D] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-1"
                      strokeWidth={2.25}
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
